import React, { memo } from 'react'
import { Typography } from '@mui/material'
import { CircleMinusMinor, CirclePlusMinor, RedoMajor, UndoMajor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  isPdf: boolean
  numPages: number
  pageNumber: number
  onPreviousPage: () => void
  onNextPage: () => void
  onZoomOut: () => void
  onZoomIn: () => void
  isZoomOutDisabled: boolean
  onRotateRight: () => void
  onRotateLeft: () => void
  filePath: string
  onDownload: () => void
  isDownloading: boolean
}

const FileViewerControlsComponent = ({
  isPdf,
  numPages,
  pageNumber,
  onPreviousPage,
  onNextPage,
  onZoomOut,
  onZoomIn,
  isZoomOutDisabled,
  onRotateRight,
  onRotateLeft,
  filePath,
  onDownload,
  isDownloading,
}: Props) => {
  return (
    <div
      css={tw`bg-shades-0 py-2 px-4 flex flex-row items-center justify-center gap-8 self-stretch shadow-lg flex-shrink-0`}
    >
      {isPdf && numPages > 1 && (
        <div css={tw`flex items-center gap-3`}>
          <Typography variant="body2" color="black">
            Page
          </Typography>
          <div css={tw`flex gap-2 items-center`}>
            <Button
              data-testid="customer-interface-file-prev-page-btn"
              onClick={onPreviousPage}
              disabled={pageNumber <= 1}
              color="white"
              size="small"
              leftIcon={
                <div css={tw`flex items-center justify-center w-full h-full`}>
                  <GSIcon
                    iconName="gs_chevron_left_small"
                    width={20}
                    height={20}
                    css={tw`text-shades-1000`}
                  />
                </div>
              }
              className={tw`rounded-lg`}
              iconOnly
            />
            <Typography variant="body2" color="black" className="min-w-[60px] text-center">
              {pageNumber} / {numPages}
            </Typography>
            <Button
              data-testid="customer-interface-file-next-page-btn"
              onClick={onNextPage}
              disabled={pageNumber >= numPages}
              color="white"
              size="small"
              leftIcon={
                <div css={tw`flex items-center justify-center w-full h-full`}>
                  <GSIcon
                    iconName="gs_chevron_right_small"
                    width={20}
                    height={20}
                    css={tw`text-shades-1000`}
                  />
                </div>
              }
              className={tw`rounded-lg`}
              iconOnly
            />
          </div>
        </div>
      )}

      <div css={tw`flex items-center gap-3`}>
        <Typography variant="body2" color="black">
          Zoom
        </Typography>
        <div css={tw`flex gap-2`}>
          <Button
            data-testid="customer-interface-file-zoom-out-btn"
            onClick={onZoomOut}
            disabled={isZoomOutDisabled}
            color="white"
            size="small"
            leftIcon={<CircleMinusMinor width={20} height={20} />}
            className={tw`rounded-lg`}
            iconOnly
          />
          <Button
            data-testid="customer-interface-file-zoom-in-btn"
            onClick={onZoomIn}
            color="white"
            size="small"
            leftIcon={<CirclePlusMinor width={20} height={20} />}
            className={tw`rounded-lg`}
            iconOnly
          />
        </div>
      </div>

      <div css={tw`flex items-center gap-3`}>
        <Typography variant="body2" color="black">
          Rotate
        </Typography>
        <div css={tw`flex gap-2`}>
          <Button
            data-testid="customer-interface-file-rotate-right-btn"
            onClick={onRotateRight}
            color="white"
            size="small"
            leftIcon={<RedoMajor width={20} height={20} />}
            className={tw`rounded-lg`}
            iconOnly
          />
          <Button
            data-testid="customer-interface-file-rotate-left-btn"
            onClick={onRotateLeft}
            color="white"
            size="small"
            leftIcon={<UndoMajor width={20} height={20} />}
            className={tw`rounded-lg`}
            iconOnly
          />
        </div>
      </div>

      {Boolean(filePath) ? (
        <Button
          data-testid="customer-interface-file-download-btn"
          onClick={onDownload}
          disabled={isDownloading}
          loading={isDownloading}
          color="white"
          size="small"
          rightIcon={
            !isDownloading ? (
              <GSIcon iconName="gs_file_download" width={20} height={20} />
            ) : undefined
          }
          className={tw`rounded-lg`}
          title="Download"
        >
          Download
        </Button>
      ) : null}
    </div>
  )
}

export const FileViewerControls = memo(FileViewerControlsComponent)
